/* mh-collapse.js — collapse groups (the footer accordion pattern, generalised).
   Groups are phone-only unless they opt in with data-mh-collapse-desktop.
   With JS off the markup ships expanded.

   Markup contract:
     [data-mh-collapse]                     group; data-mh-collapse-open="true" starts
                                            expanded on phones; data-mh-collapse-group="x"
                                            makes groups sharing the value exclusive
       [data-mh-collapse-toggle]            a real <button> with aria-controls
       [data-mh-collapse-panel]             the content, gets `hidden` on phones
     [data-mh-collapse-desktop]             also enables toggling on desktop
*/
(() => {
  if (window.MHCollapse) return;
  window.MHCollapse = true;

  const phoneQuery = window.matchMedia('(max-width: 749px)');
  const canCollapse = (group) => phoneQuery.matches || group.hasAttribute('data-mh-collapse-desktop');

  const apply = (scope) => {
    (scope || document).querySelectorAll('[data-mh-collapse]').forEach((group) => {
      // Nested groups (e.g. a collapsible section that contains a collapsible FAQ): bind only this group's own controls.
      const own = (element) => element.closest('[data-mh-collapse]') === group;
      const toggle = Array.from(group.querySelectorAll('[data-mh-collapse-toggle]')).find(own);
      const panel = Array.from(group.querySelectorAll('[data-mh-collapse-panel]')).find(own);
      if (!toggle || !panel) return;
      const enabled = canCollapse(group);
      const open = !enabled || group.dataset.mhCollapseOpen === 'true';
      toggle.setAttribute('aria-expanded', String(open));
      toggle.tabIndex = enabled ? 0 : -1;
      panel.hidden = !open;
      group.toggleAttribute('data-mh-collapsed', !open);
    });
  };

  const openGroup = (group) => {
    const key = group.dataset.mhCollapseGroup;
    if (key) {
      document.querySelectorAll(`[data-mh-collapse][data-mh-collapse-group="${key}"]`).forEach((other) => {
        if (other !== group) other.dataset.mhCollapseOpen = 'false';
      });
    }
    group.dataset.mhCollapseOpen = 'true';
  };

  // Deep links (#id inside a collapsed panel, or the group itself) open the
  // group before the browser finishes the jump.
  const reveal = (scroll = true) => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (!id) return;
    const target = document.getElementById(id);
    const group = target?.closest('[data-mh-collapse]');
    if (!group) return;
    openGroup(group);
    apply();
    if (scroll) window.requestAnimationFrame(() => target.scrollIntoView({ block: 'start' }));
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-mh-collapse-toggle]');
    if (!toggle) return;
    const group = toggle.closest('[data-mh-collapse]');
    if (!group || !canCollapse(group)) return;
    if (group.dataset.mhCollapseOpen === 'true') group.dataset.mhCollapseOpen = 'false';
    else openGroup(group);
    apply();
  });

  phoneQuery.addEventListener('change', () => apply());
  window.addEventListener('hashchange', reveal);
  document.addEventListener('shopify:section:load', (event) => apply(event.target));
  // Scripts that build groups after load (assets/mh-prose-toc.js) ask for a re-apply.
  document.addEventListener('mh:collapse:refresh', () => apply());

  const start = () => {
    apply();
    // Reload/back navigation keeps the default disclosure state and lets the
    // browser restore scrolling; a retained hash is not a new request to open.
    const navigation = window.performance?.getEntriesByType?.('navigation')[0];
    const restoring = navigation?.type === 'reload' || navigation?.type === 'back_forward';
    if (!restoring) reveal();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
